import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import ProtectedRoute from "@/context/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import DictationList from "@/pages/DictationList";
import DictationPractice from "@/pages/DictationPractice";
import LegalList from "@/pages/LegalList";
import LegalPractice from "@/pages/LegalPractice";
import PitmanList from "@/pages/PitmanList";
import PitmanPractice from "@/pages/PitmanPractice";
import TypingList from "@/pages/TypingList";
import TypingPractice from "@/pages/TypingPractice";
import History from "@/pages/History";
import Admin from "@/pages/Admin";
import Profile from "@/pages/Profile";
import Subscribe from "@/pages/Subscribe";
import Certificate from "@/pages/Certificate";
import Leaderboard from "@/pages/Leaderboard";
import Classes from "@/pages/Classes";
import PlaylistList from "@/pages/PlaylistList";
import PlaylistDetail from "@/pages/PlaylistDetail";
import LiveTestList from "@/pages/LiveTestList";
import LiveTestDetail from "@/pages/LiveTestDetail";
import Achievements from "@/pages/Achievements";
import MistakePractice from "@/pages/MistakePractice";
import RaceLobby from "@/pages/RaceLobby";
import RaceRoom from "@/pages/RaceRoom";
import Groups from "@/pages/Groups";

// Detect Emergent Google Auth callback SYNCHRONOUSLY during render so it runs
// before any ProtectedRoute checks. If the URL hash contains session_id=…,
// hand off to AuthCallback immediately.
function AppRouter() {
  const location = useLocation();
  if ((location.hash || "").includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/dictation" element={<ProtectedRoute><DictationList /></ProtectedRoute>} />
      <Route path="/dictation/:id" element={<ProtectedRoute><DictationPractice /></ProtectedRoute>} />
      <Route path="/hindi-dictation" element={<ProtectedRoute><DictationList language="hi" /></ProtectedRoute>} />
      <Route path="/hindi-dictation/:id" element={<ProtectedRoute><DictationPractice /></ProtectedRoute>} />
      <Route path="/legal" element={<ProtectedRoute><LegalList /></ProtectedRoute>} />
      <Route path="/legal/:id" element={<ProtectedRoute><LegalPractice /></ProtectedRoute>} />
      <Route path="/pitman" element={<ProtectedRoute><PitmanList /></ProtectedRoute>} />
      <Route path="/pitman/:id" element={<ProtectedRoute><PitmanPractice /></ProtectedRoute>} />
      <Route path="/typing" element={<ProtectedRoute><TypingList /></ProtectedRoute>} />
      <Route path="/typing/:id" element={<ProtectedRoute><TypingPractice /></ProtectedRoute>} />
      <Route path="/history" element={<ProtectedRoute><History /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/subscribe" element={<ProtectedRoute><Subscribe /></ProtectedRoute>} />
      <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
      <Route path="/classes" element={<ProtectedRoute><Classes /></ProtectedRoute>} />
      <Route path="/playlists" element={<ProtectedRoute><PlaylistList /></ProtectedRoute>} />
      <Route path="/playlists/:id" element={<ProtectedRoute><PlaylistDetail /></ProtectedRoute>} />
      <Route path="/live-tests" element={<ProtectedRoute><LiveTestList /></ProtectedRoute>} />
      <Route path="/live-tests/:id" element={<ProtectedRoute><LiveTestDetail /></ProtectedRoute>} />
      <Route path="/badges" element={<ProtectedRoute><Achievements /></ProtectedRoute>} />
      <Route path="/achievements" element={<Navigate to="/badges" replace />} />
      <Route path="/mistakes" element={<ProtectedRoute><MistakePractice /></ProtectedRoute>} />
      <Route path="/race" element={<ProtectedRoute><RaceLobby /></ProtectedRoute>} />
      <Route path="/race/:code" element={<ProtectedRoute><RaceRoom /></ProtectedRoute>} />
      <Route path="/groups" element={<ProtectedRoute><Groups /></ProtectedRoute>} />
      <Route path="/certificate/:id" element={<ProtectedRoute><Certificate /></ProtectedRoute>} />
      <Route path="/c/:token" element={<Certificate shareMode />} />
      <Route path="/admin" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <AppRouter />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
